import * as THREE from 'three';
import {
  requestCode, verifyCode, setDisplayName, getServers, pingServer, loadAccount, saveAccount, clearAccount,
  adminCreateWorld, adminWipeWorld, adminDeleteWorld, adminSetWorldClock, devLogin, isAdminAccount,
  type ServerInfo, type Account,
} from '../net';
import { loadSettings, saveSettings, SENS_MIN, SENS_MAX, type Settings } from '../settings';
import { buildHumanoid, getPlayerSkin, savePlayerSkin } from '../game/player';
import { TERRAIN, IS_DEV } from '../config';
import { bakeMapPreview } from './mapPreview';
import { SK } from './skin';

// THE FULL MAIN MENU (oxidegame.com front door). A two-pane window designed by the owner: an animated
// dusk-beach SCENE on the left with the OXIDE wordmark + nav, and the content on the right. It unifies the
// whole pre-game flow behind one shell:
//   AUTH      email -> 6-digit code -> (name, if new) sign-in, via the real net API
//   PLAY      server browser (real getServers) with search + region filter + favourites; a selected
//             server shows the REAL island-terrain map, wipe/pop/ping info, rules, and CONNECT (-> onSpawn)
//   PLAYER    a live 3D character preview (the real buildHumanoid model) + shirt/pants/skin swatches that
//             persist to the real player skin and show in-game
//   SETTINGS  audio/look/camera writing to the real Settings/localStorage
//   SOCIALS   community links
// Owner-designed look ported from oxide-menu-full.html; every data source here is the REAL backend.

const DEV_ACCOUNT: Account = { id: 'dev', name: 'iacon' }; // tokenless fallback only if server dev-login is off
const WIPE_DAYS = 14;                      // two-week wipe cycle (matches the guidebook)
const WIPE_EPOCH = Date.UTC(2026, 6, 4);   // last wipe anchor (2026-07-04) -> cycle currently on Day 1
const FAV_KEY = 'oxide.favServers';
const SOCIALS = [
  { nm: 'Twitter / X', ds: 'Follow for wipe news + devlogs', hd: '@playoxide', href: 'https://x.com/playoxide', sc: '#d9d2c6',
    icon: '<svg viewBox="0 0 24 24"><path d="M18.9 2H22l-7.5 8.6L23 22h-6.9l-5-6.5L5.4 22H2.3l8-9.2L1.5 2h7l4.5 6zM17 20h1.7L7 4H5.2z"/></svg>' },
  { nm: 'Discord', ds: 'Chat, LFG, and support', hd: 'discord.gg/oxide', href: 'https://discord.gg/oxide', sc: '#5865F2',
    icon: '<svg viewBox="0 0 24 24"><path d="M20 4.4A19 19 0 0 0 15.3 3l-.2.5a17 17 0 0 1 4.2 1.4A16 16 0 0 0 4.7 5 17 17 0 0 1 9 3.5L8.7 3A19 19 0 0 0 4 4.4C1.4 8.3.7 12.1 1 15.9A19 19 0 0 0 6.8 19l.9-1.3c-.8-.3-1.6-.7-2.3-1.2l.6-.4a13.6 13.6 0 0 0 11.6 0l.6.4c-.7.5-1.5.9-2.3 1.2L17.2 19a19 19 0 0 0 5.8-3.1c.4-4.4-.7-8.2-3-11.5zM8.4 13.6c-.9 0-1.7-.9-1.7-1.9s.8-1.9 1.7-1.9 1.7.9 1.7 1.9-.8 1.9-1.7 1.9zm7.2 0c-.9 0-1.7-.9-1.7-1.9s.8-1.9 1.7-1.9 1.7.9 1.7 1.9-.8 1.9-1.7 1.9z"/></svg>' },
  { nm: 'Steam', ds: 'Wishlist and reviews', hd: 'store link', href: '#', sc: '#66c0f4',
    icon: '<svg viewBox="0 0 24 24"><path d="M12 2a10 10 0 0 0-10 9.6l5.4 2.2a2.8 2.8 0 0 1 1.6-.5h.1l2.4-3.5v-.1a3.7 3.7 0 1 1 3.7 3.7h-.1l-3.4 2.5a2.8 2.8 0 0 1-5.5.8L2.4 15A10 10 0 1 0 12 2zm-3.1 15.2 1.2.5a2.1 2.1 0 1 0 1.2-4l-1.3-.5a2.1 2.1 0 0 1-1.1 4zm7.1-6.7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/></svg>' },
  { nm: 'YouTube', ds: 'Trailers and update rundowns', hd: '@playoxide', href: 'https://youtube.com/@playoxide', sc: '#ff4d4d',
    icon: '<svg viewBox="0 0 24 24"><path d="M23 7.5a3 3 0 0 0-2.1-2.1C19 4.9 12 4.9 12 4.9s-7 0-8.9.5A3 3 0 0 0 1 7.5 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.5a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.5zM9.8 15.3V8.7l5.7 3.3z"/></svg>' },
];
// Swatch palettes for the PLAYER page (map to the real numeric player skin).
const PAL = {
  shirt: [0x6a7f52, 0x8a97a0, 0x4a5568, 0xa04a30, 0x4f7a48, 0xb39a4e, 0x2f2f2f, 0x5c6b7a],
  pants: [0x2c2c2c, 0x6d8593, 0x4a5568, 0xa04a30, 0x4f7a48, 0xb39a4e, 0x242424, 0x3c4654],
  skin: [0x8a5a3c, 0xc99a66, 0xe8c9a0, 0xa5734a, 0x6b4a30, 0xcf9b6a],
};
// The RULES page (its own nav tab; the Play detail card no longer repeats these).
const RULES_LOOP = ['Spawn.', 'Survive.', 'Build Your Base.', 'Raid Others.', 'Survive the Wipe.'];
const RULES_BODY = [
  'Solos, Duos, Trios, and Quads are allowed — no bigger teams.',
  'Two-week wipe cycle — reach the rescue ship alive to carry your gear over.',
  'No cheating, scripting, or exploiting terrain/bases.',
  'Mutes, kicks, timeouts, and server or game bans may occur at admin discretion.',
];
const RULES_FUTURE = 'Future updates include tower defense and additional survival mechanics.';

type RouteKey = 'play' | 'rules' | 'player' | 'settings' | 'socials';

export class MainMenu {
  private readonly root: HTMLDivElement;
  private right!: HTMLDivElement;
  private previewToken = 0; // guards the async map preview: a newer selection bumps this so a stale bake is dropped
  private get isAdmin(): boolean { return isAdminAccount(this.account, IS_DEV); }
  private account: Account | null = null;
  private servers: ServerInfo[] | null = null;
  private serversError = '';
  // REAL measured ping per server id (round-trip to that world's DO). undefined = measuring, null = unreachable.
  private readonly pings = new Map<string, number | null>();
  private selected: ServerInfo | null = null;
  private region = 'All';
  private query = '';
  private readonly favorites = new Set<string>(loadFavServers());
  private readonly settings: Settings = loadSettings();
  private preview: { renderer: THREE.WebGLRenderer; scene: THREE.Scene; cam: THREE.PerspectiveCamera; char: THREE.Group; raf: number } | null = null;

  constructor(private readonly onSpawn: (server: ServerInfo, account: Account) => void) {
    injectCss();
    this.root = document.createElement('div');
    this.root.className = 'oxmenu';
    this.root.dataset.view = 'auth';
    this.root.innerHTML = shellHTML();
    document.body.appendChild(this.root);
    this.right = this.root.querySelector('.right') as HTMLDivElement;

    buildScene(this.root.querySelector('.scene') as HTMLDivElement);
    this.wireShell();
    this.renderRight();

    // Returning player (account saved) lands straight in the app; otherwise the sign-in flow. On LOCALHOST we
    // first try a one-click DEV sign-in (owner token) so Claude-Code Preview / local testing connects "as Dev"
    // with zero clicks -- server-gated by DEV_LOGIN, so this silently no-ops in prod.
    this.account = loadAccount();
    if (this.account) { this.enterApp(); return; }
    if (IS_DEV) { void devLogin().then((a) => { if (a && !this.account) { this.finishAuth(a); } else if (!this.account) this.showAuth(); }); return; }
    this.showAuth();
  }

  hide(): void { this.stopPreview(); this.root.style.display = 'none'; }
  destroy(): void { this.stopPreview(); this.root.remove(); }

  // --- shell (persistent left nav + footer) ----------------------------------
  private wireShell(): void {
    const nav = this.root.querySelector('#nav');
    nav?.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest('a[data-route]') as HTMLElement | null;
      if (a) this.showRoute(a.dataset.route as RouteKey);
    });
    this.root.querySelector('#logout')?.addEventListener('click', () => { clearAccount(); this.account = null; this.showAuth(); });
  }

  private renderRight(): void {
    this.right.innerHTML =
      `<div class="pad app-only">${routePlay()}${routeRules()}${routePlayer()}${routeSettings(this.settings)}${routeSocials()}</div>` +
      authHTML(import.meta.env.DEV);
    this.wirePlay();
    this.wirePlayer();
    this.wireSettings();
    this.wireAuth();
  }

  // --- auth ------------------------------------------------------------------
  private showAuth(): void {
    this.root.dataset.view = 'auth';
    this.stopPreview();
    this.right.querySelectorAll('.auth-err').forEach((e) => (e.textContent = '')); // no stale error when returning to the email step
    const em = this.right.querySelector('#step-email') as HTMLElement | null;
    const cd = this.right.querySelector('#step-code') as HTMLElement | null;
    const nm = this.right.querySelector('#step-name') as HTMLElement | null;
    if (em) em.style.display = 'block';
    if (cd) cd.style.display = 'none';
    if (nm) nm.style.display = 'none';
    (this.right.querySelector('#email') as HTMLInputElement | null)?.focus();
  }

  private enterApp(): void {
    this.root.dataset.view = 'app';
    (this.root.querySelector('#usr') as HTMLElement).textContent = this.account?.name ?? '';
    this.showRoute('play');
    void this.loadServers();
  }

  private wireAuth(): void {
    const q = <T extends HTMLElement>(s: string) => this.right.querySelector(s) as T | null;
    const email = q<HTMLInputElement>('#email'), code = q<HTMLInputElement>('#code'), name = q<HTMLInputElement>('#name');
    const stepEmail = q<HTMLElement>('#step-email'), stepCode = q<HTMLElement>('#step-code'), stepName = q<HTMLElement>('#step-name');
    const setErr = (m: string) => this.right.querySelectorAll('.auth-err').forEach((e) => (e.textContent = m));
    const busy = (b: HTMLButtonElement | null, on: boolean, label: string) => { if (b) { b.disabled = on; b.textContent = on ? label : b.dataset.label ?? label; } };

    const send = q<HTMLButtonElement>('#send');
    send && (send.onclick = async () => {
      const e = (email?.value ?? '').trim(); setErr('');
      if (!e) { email?.focus(); return; }
      busy(send, true, 'Sending...');
      try {
        const res = await requestCode(e);
        if (stepEmail) stepEmail.style.display = 'none';
        if (stepCode) stepCode.style.display = 'block';
        const sub = q<HTMLElement>('#code-sub');
        if (sub) sub.textContent = res.devCode ? `Dev code: ${res.devCode} — email service skipped.` : `Enter the 6-digit code sent to ${e}.`;
        if (code) { code.value = ''; code.focus(); }
        this.authEmail = e;
      } catch (ex) { setErr(errMsg(ex)); } finally { busy(send, false, 'Send code'); }
    });

    const verify = q<HTMLButtonElement>('#verify');
    verify && (verify.onclick = async () => {
      setErr(''); busy(verify, true, 'Verifying...');
      try {
        const r = await verifyCode(this.authEmail, (code?.value ?? '').trim());
        if (r.displayName) { this.finishAuth({ id: r.id, name: r.displayName, token: r.token }); }
        else { this.pendingId = r.id; this.pendingToken = r.token; if (stepCode) stepCode.style.display = 'none'; if (stepName) stepName.style.display = 'block'; name?.focus(); }
      } catch (ex) { setErr(errMsg(ex)); } finally { busy(verify, false, 'Verify & enter'); }
    });

    const saveName = q<HTMLButtonElement>('#save-name');
    saveName && (saveName.onclick = async () => {
      setErr(''); busy(saveName, true, 'Saving...');
      try { const r = await setDisplayName(this.pendingId, (name?.value ?? '').trim()); this.finishAuth({ id: r.id, name: r.displayName, token: r.token ?? this.pendingToken }); }
      catch (ex) { setErr(errMsg(ex)); } finally { busy(saveName, false, 'Continue'); }
    });

    q<HTMLElement>('#back')?.addEventListener('click', () => this.showAuth());
    // Enter submits the visible step.
    email?.addEventListener('keydown', (e) => { if (e.key === 'Enter') send?.click(); });
    code?.addEventListener('keydown', (e) => { if (e.key === 'Enter') verify?.click(); });
    name?.addEventListener('keydown', (e) => { if (e.key === 'Enter') saveName?.click(); });

    // DEV skip: join the test server directly (only compiled into the dev build).
    const skip = q<HTMLButtonElement>('#skip');
    // Skip = one-click dev sign-in (owner token from /api/auth/dev), then land in the app. Falls back to the
    // tokenless DEV_ACCOUNT only if dev login is disabled server-side (then joins will fail with a clear reason).
    skip && (skip.onclick = async () => { const a = await devLogin(); this.finishAuth(a ?? DEV_ACCOUNT); });
  }
  private authEmail = '';
  private pendingId = '';
  private pendingToken: string | undefined; // session token minted at verify, carried into the name step

  private finishAuth(a: Account): void { saveAccount(a); this.account = a; this.enterApp(); }

  // --- routing ---------------------------------------------------------------
  private showRoute(name: RouteKey): void {
    this.root.querySelectorAll('.route').forEach((r) => r.classList.remove('on'));
    this.right.querySelector('#r-' + name)?.classList.add('on');
    this.root.querySelectorAll('#nav a').forEach((a) => a.classList.toggle('active', (a as HTMLElement).dataset.route === name));
    if (name === 'player') this.startPreview();
    else this.stopPreview();
  }

  // --- PLAY ------------------------------------------------------------------
  private async loadServers(): Promise<void> {
    try { this.servers = await getServers(); this.serversError = ''; }
    catch (e) { this.serversError = e instanceof Error ? e.message : 'Could not load servers.'; this.servers = []; }
    this.renderRegionChips();
    this.renderList();
    void this.measurePings();
  }

  // Measure the REAL round-trip to every listed world (in parallel) and refresh its live count. Re-renders as
  // results land so the rows update from "..." to the measured number without blocking the list itself.
  private async measurePings(): Promise<void> {
    await Promise.all((this.servers ?? []).map(async (s) => {
      const { ping, players } = await pingServer(s.id);
      this.pings.set(s.id, ping);
      if (players !== null) s.players = players; // freshest count (the /api/servers one may be seconds old)
      this.renderList();
      if (this.selected?.id === s.id) this.renderDetail();
    }));
  }

  private wirePlay(): void {
    const search = this.right.querySelector('#search') as HTMLInputElement | null;
    if (search) { search.value = this.query; search.addEventListener('input', () => { this.query = search.value; this.renderList(); }); }
    this.right.querySelector('#srlist')?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const star = t.closest('.star') as HTMLElement | null;
      const row = t.closest('.srow') as HTMLElement | null;
      if (!row) return;
      const s = (this.servers ?? []).find((x) => x.id === row.dataset.id);
      if (!s) return;
      if (star) { this.toggleFav(s.id); this.renderList(); if (this.selected?.id === s.id) this.renderDetail(); return; }
      this.selected = s; this.renderList(); this.renderDetail();
    });
    const nw = this.right.querySelector('#newworld') as HTMLButtonElement | null;
    if (nw && this.isAdmin) { nw.style.display = ''; nw.onclick = () => this.promptCreateWorld(); }
    this.renderRegionChips();
    this.renderList();
    this.renderDetail();
  }

  private renderRegionChips(): void {
    const bar = this.right.querySelector('#chips'); if (!bar) return;
    const regions = ['All', ...[...new Set((this.servers ?? []).map((s) => s.region))].sort()];
    bar.innerHTML = regions.map((r) => `<div class="chip${r === this.region ? ' on' : ''}" data-region="${esc(r)}">${esc(r)}</div>`).join('');
    bar.querySelectorAll('.chip').forEach((c) => c.addEventListener('click', () => { this.region = (c as HTMLElement).dataset.region!; this.renderRegionChips(); this.renderList(); }));
  }

  private renderList(): void {
    const list = this.right.querySelector('#srlist'); if (!list) return;
    if (this.servers === null) { list.innerHTML = `<div class="empty-note">Loading servers...</div>`; return; }
    if (this.serversError) { list.innerHTML = `<div class="empty-note" style="color:var(--ox-red-soft)">${esc(this.serversError)}</div>`; return; }
    const shown = this.servers
      .filter((s) => (this.region === 'All' || s.region === this.region) && s.name.toLowerCase().includes(this.query.toLowerCase()))
      .sort((a, b) => (Number(this.favorites.has(b.id)) - Number(this.favorites.has(a.id))) || a.name.localeCompare(b.name));
    list.innerHTML = shown.length ? shown.map((s) => srow(s, this.selected?.id === s.id, this.favorites.has(s.id), this.pings.get(s.id))).join('')
      : `<div class="empty-note">No servers match.</div>`;
  }

  private renderDetail(): void {
    const box = this.right.querySelector('#detail'); if (!box) return;
    const s = this.selected;
    if (!s) { box.innerHTML = `<div class="detail empty">Select a server to see its rules,<br>map and wipe schedule.</div>`; return; }
    const m = serverMeta(s);
    box.innerHTML = `<div class="detail">
      <div class="mapbox"><span class="mapN">N &#9650;</span></div>
      <div style="display:flex;align-items:center;gap:8px">
        <div style="font-family:var(--mono);font-weight:600;font-size:15px;flex:1">${esc(s.name)}</div>
        <span class="star det${this.favorites.has(s.id) ? ' on' : ''}" data-fav style="font-size:18px">${this.favorites.has(s.id) ? '&#9733;' : '&#9734;'}</span>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap">${m.tags.map((t) => `<span class="tag">${t}</span>`).join('')}</div>
      <div class="kv"><span>Population</span><span>${m.players}/${s.maxPlayers}</span></div>
      <div class="pop"><i style="width:${Math.round(m.players / Math.max(1, s.maxPlayers) * 100)}%"></i></div>
      <div class="kv"><span>Wipe</span><span>Day ${m.day}/${WIPE_DAYS} · in ${m.daysLeft}d</span></div>
      <div class="kv"><span>Region</span><span>${esc(s.region)}</span></div>
      <div class="kv"><span>Ping</span><span>${pingText(this.pings.get(s.id))}</span></div>
      <button class="btn red" style="margin-top:auto" id="connect">CONNECT</button>
      ${this.isAdmin ? `
      <div class="time-row">
        <span class="time-lbl">Time</span>
        <input type="range" id="timefrac" min="0" max="1" step="0.001" value="0.5">
        <span class="time-val" id="timeval">12:00</span>
        <label class="time-lock"><input type="checkbox" id="timelock"> Lock</label>
        <button class="btn" id="timeset" style="width:auto;padding:6px 12px;font-size:11px">Set</button>
      </div>
      <div style="display:flex;gap:6px"><button class="btn" id="wipe" style="flex:1;font-size:11px;padding:7px;background:transparent;border-color:var(--ox-bad);color:var(--ox-bad)">WIPE</button><button class="btn" id="delserver" style="flex:1;font-size:11px;padding:7px;background:transparent;border-color:var(--ox-bad);color:var(--ox-bad)">DELETE</button></div>` : ''}
    </div>`;
    // DELETE (admin): two-click armed, like WIPE. Removes the world from the list entirely (the fallback test-1
    // is protected server-side -> it just wipes). Refreshes the browser after.
    if (this.isAdmin) {
      const del = box.querySelector('#delserver') as HTMLButtonElement | null;
      if (del) { let armed = false; let t = 0; del.onclick = async () => {
        if (!armed) { armed = true; del.textContent = 'CONFIRM DELETE'; del.style.background = SK.badSoft; t = window.setTimeout(() => { armed = false; del.textContent = 'DELETE'; del.style.background = 'transparent'; }, 4000); return; }
        clearTimeout(t); del.textContent = 'DELETING…'; del.disabled = true;
        try {
          await adminDeleteWorld(this.account!, s.id);
          purgeMapCache(s.id); // a deleted world takes its cached map + map LOD with it
          this.servers = (this.servers ?? []).filter((x) => x.id !== s.id); this.selected = this.servers[0] ?? null; this.renderList(); this.renderDetail();
        }
        catch (e) { del.disabled = false; del.textContent = 'DELETE'; this.adminMsg(e instanceof Error ? e.message : 'Delete failed.', 'err'); }
      }; }
    }
    // Real island map for THIS world's SEED. Square box (full map, never cropped); cached per world so a
    // re-select is instant; CLICK opens the full-size zoom/pan viewer. previewToken drops a stale bake if
    // the user clicks a different server before this one finishes.
    const mapbox = box.querySelector('.mapbox') as HTMLElement | null;
    if (mapbox) {
      mapbox.title = 'Click to view the full map';
      mapbox.addEventListener('click', () => this.openMapViewer(s));
      const key = mapCacheKey(s.id, s.seed, false);
      const place = (src: string): void => {
        const img = new Image(); img.src = src; img.draggable = false;
        img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block';
        mapbox.querySelector('.mapload')?.remove();
        mapbox.prepend(img);
      };
      const cached = readMapCache(key);
      if (cached) place(cached);
      else {
        const token = ++this.previewToken;
        mapbox.innerHTML = `<div class="mapload"><span>generating map…</span><div class="mbar"><i></i></div></div><span class="mapN">N &#9650;</span>`;
        const fill = mapbox.querySelector('.mbar i') as HTMLElement | null;
        void bakeMapPreview(s.seed ?? TERRAIN.seed, 260, (f) => { if (fill) fill.style.width = Math.round(f * 100) + '%'; }).then((canvas) => {
          if (token !== this.previewToken) return; // a newer selection won
          writeMapCache(key, canvas);
          place(canvas.toDataURL('image/webp', 0.85));
        });
      }
    }
    box.querySelector('[data-fav]')?.addEventListener('click', () => { this.toggleFav(s.id); this.renderList(); this.renderDetail(); });
    const c = box.querySelector('#connect') as HTMLButtonElement | null;
    c && (c.onclick = () => { if (!this.account) return; c.textContent = 'CONNECTING...'; c.disabled = true; this.onSpawn(s, this.account); });
    // Admin TIME LOCK: pin (or free) an EXISTING world's day-night clock right now -- fixes a test world
    // that's stuck at night mid-session without having to recreate it. Write-only (like WIPE/DELETE): it
    // doesn't read the world's current lock state back, it just applies whatever the slider says on Set.
    if (this.isAdmin) {
      const tf = box.querySelector('#timefrac') as HTMLInputElement | null;
      const tv = box.querySelector('#timeval') as HTMLElement | null;
      const tset = box.querySelector('#timeset') as HTMLButtonElement | null;
      if (tf && tv) tf.addEventListener('input', () => { tv.textContent = timeLabel(Number(tf.value)); });
      if (tset && tf) {
        tset.addEventListener('click', async () => {
          const locked = (box.querySelector('#timelock') as HTMLInputElement).checked;
          tset.disabled = true; tset.textContent = 'Setting…';
          try { await adminSetWorldClock(this.account!, s.id, Number(tf.value), locked); this.adminMsg(locked ? `Time locked at ${timeLabel(Number(tf.value))}.` : 'Time unlocked — free-running again.', 'ok'); }
          catch (e) { this.adminMsg(e instanceof Error ? e.message : 'Set time failed.', 'err'); }
          finally { tset.disabled = false; tset.textContent = 'Set'; }
        });
      }
    }
    // Admin WIPE: two-click confirm (this deletes the whole world), then hit the server.
    const w = box.querySelector('#wipe') as HTMLButtonElement | null;
    if (w) {
      let armed = false, armT = 0;
      w.onclick = () => {
        if (!armed) { armed = true; w.textContent = 'CLICK AGAIN TO WIPE'; w.style.background = SK.badSoft; armT = window.setTimeout(() => { armed = false; w.textContent = 'WIPE WORLD'; w.style.background = 'transparent'; }, 4000); return; }
        window.clearTimeout(armT); void this.wipeWorld(s, w);
      };
    }
  }

  // Admin: create a world through a MAP-GENERATION POPUP -- preview the island a seed generates,
  // RANDOMIZE until it isn't broken (all sea / shredded coast happens), then Accept ships THAT seed.
  private promptCreateWorld(): void {
    if (!this.account) return;
    const ov = document.createElement('div');
    ov.className = 'oxmenu-mapview';
    ov.innerHTML = `<div class="mv-frame nw">
      <div class="mv-head"><span>New World — map generation</span><div class="mv-btns"><button data-mv="x">✕</button></div></div>
      <div class="nw-body">
        <div class="nw-map"><span class="mapN">N &#9650;</span></div>
        <div class="nw-side">
          <label>World name</label><input id="nwname" maxlength="30" placeholder="New World">
          <label>Seed</label><div class="nw-seed" id="nwseed">—</div>
          <label>Time of day</label>
          <div class="time-row" style="padding:0">
            <input type="range" id="nwtime" min="0" max="1" step="0.001" value="0.5">
            <span class="time-val" id="nwtimeval">12:00</span>
            <label class="time-lock"><input type="checkbox" id="nwlock"> Lock (never changes)</label>
          </div>
          <div class="nw-note">Lock for a dedicated test world (e.g. always-lit for gun-fit tuning) — otherwise it free-runs on the shared clock from this starting time. Randomize until the island looks right — some generations are broken. Accept creates the world with exactly this map.</div>
          <button class="btn" id="nwrand">&#8635; Randomize</button>
          <button class="btn red" id="nwgo">Accept &amp; create</button>
          <div class="nw-err" id="nwerr"></div>
        </div>
      </div></div>`;
    document.body.appendChild(ov);
    const q = <T extends HTMLElement>(sel: string) => ov.querySelector(sel) as T;
    const mapEl = q<HTMLElement>('.nw-map'), seedEl = q<HTMLElement>('#nwseed'), errEl = q<HTMLElement>('#nwerr');
    const nameIn = q<HTMLInputElement>('#nwname'), rand = q<HTMLButtonElement>('#nwrand'), go = q<HTMLButtonElement>('#nwgo');
    const timeIn = q<HTMLInputElement>('#nwtime'), timeVal = q<HTMLElement>('#nwtimeval'), lockIn = q<HTMLInputElement>('#nwlock');
    timeIn.addEventListener('input', () => { timeVal.textContent = timeLabel(Number(timeIn.value)); });
    let seed = randomSeed(), bakeToken = 0, lastCanvas: HTMLCanvasElement | null = null;
    const bake = (): void => {
      const token = ++bakeToken;
      seedEl.textContent = String(seed);
      mapEl.innerHTML = `<div class="mapload"><span>generating…</span><div class="mbar"><i></i></div></div><span class="mapN">N &#9650;</span>`;
      const fill = mapEl.querySelector('.mbar i') as HTMLElement | null;
      void bakeMapPreview(seed, 340, (f) => { if (fill) fill.style.width = Math.round(f * 100) + '%'; }).then((canvas) => {
        if (token !== bakeToken || !ov.isConnected) return;
        lastCanvas = canvas;
        canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block';
        mapEl.querySelector('.mapload')?.remove();
        mapEl.prepend(canvas);
      });
    };
    const close = (): void => { ov.remove(); document.removeEventListener('keydown', onEsc, true); };
    const onEsc = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } };
    document.addEventListener('keydown', onEsc, true);
    ov.addEventListener('pointerdown', (e) => { if (e.target === ov) close(); });
    q<HTMLElement>('[data-mv="x"]').onclick = close;
    rand.onclick = () => { seed = randomSeed(); bake(); };
    go.onclick = async (): Promise<void> => {
      const name = nameIn.value.trim() || 'New World';
      go.disabled = true; rand.disabled = true; go.textContent = 'Creating…'; errEl.textContent = '';
      try {
        const server = await adminCreateWorld(this.account!, name, seed, Number(timeIn.value), lockIn.checked);
        if (lastCanvas) writeMapCache(mapCacheKey(server.id, server.seed, false), lastCanvas); // the card preview is already baked
        this.servers = [...(this.servers ?? []), server];
        close();
        this.renderRegionChips(); this.renderList();
        this.selected = server; this.renderDetail();
        this.adminMsg(`Created "${server.name}" (seed ${server.seed}).`, 'ok');
      } catch (e) { go.disabled = false; rand.disabled = false; go.textContent = 'Accept & create'; errEl.textContent = e instanceof Error ? e.message : 'Create failed.'; }
    };
    nameIn.focus();
    bake();
  }

  // Full-size map viewer: zoom (wheel / buttons, cursor-centred) + drag pan. The 768px bake is cached
  // per world as its map LOD -- deleting the world purges it (see the DELETE handler).
  private openMapViewer(s: ServerInfo): void {
    const ov = document.createElement('div');
    ov.className = 'oxmenu-mapview';
    ov.innerHTML = `<div class="mv-frame">
      <div class="mv-head"><span>${esc(s.name)} — island map</span><span class="mv-hint">wheel to zoom · drag to pan</span>
        <div class="mv-btns"><button data-mv="in">+</button><button data-mv="out">&minus;</button><button data-mv="x">✕</button></div></div>
      <div class="mv-body"></div></div>`;
    document.body.appendChild(ov);
    const body = ov.querySelector('.mv-body') as HTMLElement;
    let scale = 1, tx = 0, ty = 0, img: HTMLImageElement | null = null;
    const apply = (): void => { if (img) img.style.transform = `translate(${tx}px,${ty}px) scale(${scale})`; };
    const clampZ = (v: number): number => Math.max(1, Math.min(8, v));
    const land = (src: string): void => {
      img = new Image(); img.src = src; img.className = 'mv-img'; img.draggable = false;
      body.querySelector('.mapload')?.remove(); body.appendChild(img); apply();
    };
    const key = mapCacheKey(s.id, s.seed, true);
    const cached = readMapCache(key);
    if (cached) land(cached);
    else {
      body.innerHTML = `<div class="mapload"><span>generating full map…</span><div class="mbar"><i></i></div></div>`;
      const fill = body.querySelector('.mbar i') as HTMLElement | null;
      void bakeMapPreview(s.seed ?? TERRAIN.seed, 768, (f) => { if (fill) fill.style.width = Math.round(f * 100) + '%'; }).then((canvas) => {
        if (!ov.isConnected) return;
        writeMapCache(key, canvas);
        land(canvas.toDataURL('image/webp', 0.85));
      });
    }
    body.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = body.getBoundingClientRect();
      const mx = e.clientX - r.left - r.width / 2, my = e.clientY - r.top - r.height / 2;
      const ns = clampZ(scale * (e.deltaY < 0 ? 1.2 : 1 / 1.2)), k = ns / scale;
      tx = mx - k * (mx - tx); ty = my - k * (my - ty); scale = ns;
      if (scale === 1) { tx = 0; ty = 0; }
      apply();
    }, { passive: false });
    let drag: { x: number; y: number } | null = null;
    body.addEventListener('pointerdown', (e) => { drag = { x: e.clientX - tx, y: e.clientY - ty }; body.setPointerCapture(e.pointerId); });
    body.addEventListener('pointermove', (e) => { if (drag) { tx = e.clientX - drag.x; ty = e.clientY - drag.y; apply(); } });
    body.addEventListener('pointerup', () => { drag = null; });
    const close = (): void => { ov.remove(); document.removeEventListener('keydown', onEsc, true); };
    const onEsc = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } };
    document.addEventListener('keydown', onEsc, true);
    ov.addEventListener('pointerdown', (e) => { if (e.target === ov) close(); });
    (ov.querySelector('[data-mv="x"]') as HTMLElement).onclick = close;
    (ov.querySelector('[data-mv="in"]') as HTMLElement).onclick = () => { scale = clampZ(scale * 1.4); apply(); };
    (ov.querySelector('[data-mv="out"]') as HTMLElement).onclick = () => { scale = clampZ(scale / 1.4); if (scale === 1) { tx = 0; ty = 0; } apply(); };
  }

  private async wipeWorld(s: ServerInfo, btn: HTMLButtonElement): Promise<void> {
    if (!this.account) return;
    btn.disabled = true; btn.textContent = 'WIPING…';
    try {
      await adminWipeWorld(this.account, s.id);
      btn.textContent = 'WIPED ✓'; btn.style.background = 'rgba(120,200,120,0.18)';
      this.adminMsg(`Wiped "${s.name}" — fresh island. Anyone in it needs to reload.`, 'ok');
    } catch (e) { btn.disabled = false; btn.textContent = 'WIPE WORLD'; btn.style.background = ''; this.adminMsg(e instanceof Error ? e.message : 'Wipe failed.', 'err'); }
  }

  private adminMsg(text: string, kind: 'ok' | 'err'): void {
    const el = this.right.querySelector('#admin-msg') as HTMLElement | null; if (!el) return;
    el.textContent = text; el.style.display = ''; el.style.color = kind === 'ok' ? SK.good : SK.bad;
    window.setTimeout(() => { if (el.textContent === text) el.style.display = 'none'; }, 6000);
  }

  private toggleFav(id: string): void { if (this.favorites.has(id)) this.favorites.delete(id); else this.favorites.add(id); saveFavServers(this.favorites); }

  // --- PLAYER (3D preview + swatches -> real skin) ---------------------------
  private wirePlayer(): void {
    const wrap = this.right.querySelector('#r-player'); if (!wrap) return;
    this.renderSwatches();
    wrap.querySelector('#swatches')?.addEventListener('click', (e) => {
      const sw = (e.target as HTMLElement).closest('.sw') as HTMLElement | null; if (!sw) return;
      const type = sw.dataset.type as 'shirt' | 'pants' | 'skin';
      const color = parseInt(sw.dataset.color!, 10);
      const skin = getPlayerSkin(); skin[type] = color; savePlayerSkin(skin);
      this.renderSwatches();
      this.rebuildPreviewChar();
    });
  }

  private renderSwatches(): void {
    const host = this.right.querySelector('#swatches'); if (!host) return;
    const skin = getPlayerSkin();
    const rowFor = (label: string, type: 'shirt' | 'pants' | 'skin') =>
      `<div class="swrow"><span class="lbl">${label}</span><div class="swatches">${PAL[type].map((c) =>
        `<span class="sw${skin[type] === c ? ' on' : ''}" data-type="${type}" data-color="${c}" style="background:${hex(c)}"></span>`).join('')}</div></div>`;
    host.innerHTML = rowFor('Shirt', 'shirt') + rowFor('Pants', 'pants') + rowFor('Skin tone', 'skin');
  }

  private startPreview(): void {
    const box = this.right.querySelector('#char-preview') as HTMLElement | null;
    if (!box || this.preview) return;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block';
    box.appendChild(canvas);
    const W = box.clientWidth || 300, H = box.clientHeight || 400;
    canvas.width = W; canvas.height = H;
    try {
      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
      renderer.setSize(W, H, false);
      const scene = new THREE.Scene();
      scene.add(new THREE.HemisphereLight(0xdfeaff, 0x3a3a44, 1.25));
      const dir = new THREE.DirectionalLight(0xffffff, 1.1); dir.position.set(2, 5, 3); scene.add(dir);
      const char = buildHumanoid().body; scene.add(char);
      const cam = new THREE.PerspectiveCamera(30, W / H, 0.1, 100);
      cam.position.set(0, 1.05, 4.8); cam.lookAt(0, 0.95, 0);
      this.preview = { renderer, scene, cam, char, raf: 0 };
      const step = (): void => {
        if (!this.preview) return;
        this.preview.char.rotation.y += 0.007;
        renderer.render(this.preview.scene, this.preview.cam);
        this.preview.raf = requestAnimationFrame(step);
      };
      this.preview.raf = requestAnimationFrame(step);
    } catch { canvas.remove(); /* no webgl: the styled box is the fallback */ }
  }

  private rebuildPreviewChar(): void {
    if (!this.preview) return;
    this.preview.scene.remove(this.preview.char);
    disposeObject(this.preview.char); // free the old model's geometry/materials (buildHumanoid allocates fresh, unshared ones)
    this.preview.char = buildHumanoid().body; // reads the freshly-saved skin
    this.preview.scene.add(this.preview.char);
  }

  private stopPreview(): void {
    if (!this.preview) return;
    cancelAnimationFrame(this.preview.raf);
    disposeObject(this.preview.char); // renderer.dispose() does NOT walk the scene graph, so free the char's GPU buffers here
    this.preview.renderer.dispose();
    this.preview.renderer.domElement.remove();
    this.preview = null;
  }

  // --- SETTINGS (-> real Settings) -------------------------------------------
  private wireSettings(): void {
    const q = <T extends HTMLElement>(s: string) => this.right.querySelector(s) as T | null;
    const slider = (id: string, valId: string, fmt: (v: number) => string, apply: (v: number) => void) => {
      const el = q<HTMLInputElement>(id); const out = q<HTMLElement>(valId); if (!el) return;
      const set = () => {
        const min = +el.min, max = +el.max, pct = ((+el.value - min) / (max - min)) * 100;
        el.style.setProperty('--val', pct + '%');
        if (out) out.textContent = fmt(+el.value);
      };
      el.addEventListener('input', () => { set(); apply(+el.value); });
      set();
    };
    const s = this.settings; const save = () => saveSettings(s);
    slider('#s-vol', '#v-vol', (v) => v.toFixed(2), (v) => { s.volume = v; save(); });
    slider('#s-sens', '#v-sens', (v) => v.toFixed(2) + 'x', (v) => { s.lookSensitivity = v; save(); });
    slider('#s-fov', '#v-fov', (v) => String(Math.round(v)), (v) => { s.fpFov = v; save(); });
    const check = (id: string, apply: (b: boolean) => void) => { const el = q<HTMLInputElement>(id); el?.addEventListener('change', () => { apply(el.checked); save(); }); };
    check('#c-mmb', (b) => { s.invertMmbY = b; });
    check('#c-alt', (b) => { s.invertAltY = b; });
    check('#c-bob', (b) => { s.headbob = b; });
    check('#c-banim', (b) => { s.buildAnim = b; });
  }
}

// ============================ static templates =============================
function shellHTML(): string {
  return `<div class="window">
    <div class="left">
      <div class="scene"></div>
      <div class="scrim"></div><div class="vig"></div>
      <div class="loverlay">
        <div class="wm">OX<b>IDE</b></div>
        <div class="auth-only">
          <div class="tagline">Survive the island. Build. Raid. Wipe. Repeat.</div>
          <div class="devtag">v0.1 &middot; DEV BUILD</div>
        </div>
        <div class="app-only" style="display:flex;flex-direction:column;flex:1;min-height:0">
          <nav class="nav" id="nav">
            <a data-route="play" class="active">Play</a>
            <a data-route="rules">Rules</a>
            <a data-route="player">Player</a>
            <a data-route="settings">Settings</a>
            <a data-route="socials">Socials</a>
          </nav>
          <div class="foot">
            <div class="lbl">Signed in</div>
            <div class="usr" id="usr"></div>
            <span class="out" id="logout">Log out</span>
          </div>
        </div>
      </div>
    </div>
    <div class="right"></div>
  </div>`;
}

function routePlay(): string {
  return `<div class="route" id="r-play">
    <div class="ptitle">Server Browser</div>
    <div class="psub">Pick a server to join. Click one for its rules, map and wipe info.</div>
    <div class="toolbar">
      <div class="search"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink-faint)" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
        <input id="search" placeholder="Search by name..."></div>
      <div id="chips" style="display:flex;gap:10px"></div>
      <button id="newworld" class="btn" style="display:none;margin-left:auto;font-size:11px;padding:6px 12px">+ New World</button>
    </div>
    <div id="admin-msg" style="display:none;font-family:var(--mono);font-size:11px;margin:-4px 0 8px"></div>
    <div class="playgrid">
      <div class="srlist" id="srlist"></div>
      <div id="detail"></div>
    </div>
  </div>`;
}
function routeRules(): string {
  return `<div class="route" id="r-rules">
    <div class="ptitle">Rules</div>
    <div class="psub">Simple. Learn the loop, respect the limits.</div>
    <div class="rloop">${RULES_LOOP.map((r, i) => `<div class="rstep"><span class="n">${i + 1}</span>${esc(r)}</div>`).join('')}</div>
    <div class="section-lbl">The fine print</div>
    <ul style="margin:0;padding-left:17px;font-family:var(--mono);font-size:12.5px;color:var(--ink-dim);line-height:2">${RULES_BODY.map((r) => `<li>${esc(r)}</li>`).join('')}</ul>
    <div class="section-lbl">What's coming</div>
    <div style="font-family:var(--mono);font-size:12.5px;color:var(--ink-dim);line-height:1.7">${esc(RULES_FUTURE)}</div>
  </div>`;
}
function routePlayer(): string {
  return `<div class="route" id="r-player">
    <div class="ptitle">Player</div>
    <div class="psub">Customise how you look. Your choices show on your character in-game.</div>
    <div class="playerwrap">
      <div class="preview" id="char-preview"></div>
      <div>
        <div class="section-lbl">Appearance</div>
        <div id="swatches"></div>
        <div class="section-lbl">Stats</div>
        <div class="statgrid">${[['—', 'Hours played'], ['—', 'Wipes survived'], ['—', 'Bases raided'], ['—', 'Longest life']]
      .map((st) => `<div class="stat"><div class="v">${st[0]}</div><div class="k">${st[1]}</div></div>`).join('')}</div>
      </div>
    </div>
  </div>`;
}
function routeSettings(s: Settings): string {
  const ck = (b: boolean) => (b ? ' checked' : '');
  return `<div class="route" id="r-settings">
    <div class="ptitle">Settings</div>
    <div class="psub">Applied everywhere. Rebind keys in-game (Esc &rarr; Settings).</div>
    <div class="section-lbl">Audio</div>
    <div class="rangewrap"><div class="setrow"><div class="top"><span>Master volume</span><span class="val" id="v-vol">${s.volume.toFixed(2)}</span></div>
      <input type="range" id="s-vol" min="0" max="1" step="0.01" value="${s.volume}"></div></div>
    <div class="section-lbl">Look</div>
    <div class="rangewrap"><div class="setrow"><div class="top"><span>Sensitivity</span><span class="val" id="v-sens">${s.lookSensitivity.toFixed(2)}x</span></div>
      <input type="range" id="s-sens" min="${SENS_MIN}" max="${SENS_MAX}" step="0.05" value="${s.lookSensitivity}"></div></div>
    <div class="checkrow"><span>Invert vertical (middle-mouse)</span><input type="checkbox" id="c-mmb"${ck(s.invertMmbY)}></div>
    <div class="checkrow"><span>Invert vertical (Alt free-look)</span><input type="checkbox" id="c-alt"${ck(s.invertAltY)}></div>
    <div class="section-lbl">Camera</div>
    <div class="rangewrap"><div class="setrow"><div class="top"><span>First-person FOV</span><span class="val" id="v-fov">${Math.round(s.fpFov)}</span></div>
      <input type="range" id="s-fov" min="60" max="110" step="1" value="${s.fpFov}"></div></div>
    <div class="checkrow"><span>First-person head-bob</span><input type="checkbox" id="c-bob"${ck(s.headbob)}></div>
    <div class="checkrow"><span>Building place animation</span><input type="checkbox" id="c-banim"${ck(s.buildAnim)}></div>
  </div>`;
}
function routeSocials(): string {
  return `<div class="route" id="r-socials">
    <div class="ptitle">Socials</div>
    <div class="psub">Join the community. News, updates, and finding a team.</div>
    <div class="socgrid">${SOCIALS.map((s) => `<a class="soc" href="${s.href}" target="_blank" rel="noopener noreferrer" style="--sc:${s.sc}">
      <div class="ico">${s.icon}</div>
      <div><div class="nm">${s.nm}</div><div class="ds">${s.ds}</div><div class="hd">${s.hd}</div></div></a>`).join('')}</div>
  </div>`;
}
function authHTML(dev: boolean): string {
  return `<div class="auth-wrap auth-only"><div class="auth-card">
    <div id="step-email">
      <h2>Sign in</h2><p>Enter your email to get a verification code.</p>
      <input class="field" id="email" type="email" placeholder="you@example.com">
      <div class="auth-err" id="auth-err"></div>
      <button class="btn primary" id="send" data-label="Send code">Send code</button>
      ${dev ? '<button class="btn dev" id="skip">Skip &rarr; join test server (dev)</button>' : ''}
    </div>
    <div id="step-code" style="display:none">
      <h2>Check your email</h2><p id="code-sub">Enter the 6-digit code we sent.</p>
      <input class="field code" id="code" maxlength="6" inputmode="numeric" placeholder="000000">
      <div class="auth-err" id="auth-err-c"></div>
      <button class="btn red" id="verify" data-label="Verify & enter">Verify &amp; enter</button>
      <div class="subtle" id="back">&larr; Use a different email</div>
    </div>
    <div id="step-name" style="display:none">
      <h2>Choose a name</h2><p>How others will see you. Up to 30 characters.</p>
      <input class="field" id="name" maxlength="30" placeholder="Survivor">
      <div class="auth-err"></div>
      <button class="btn primary" id="save-name" data-label="Continue">Continue</button>
    </div>
  </div></div>`;
}
function srow(s: ServerInfo, sel: boolean, faved: boolean, ping?: number | null): string {
  const m = serverMeta(s);
  const live = ping !== null; // null = the measurement round-trip failed -> the world is unreachable right now
  return `<div class="srow${sel ? ' sel' : ''}" data-id="${esc(s.id)}">
    <span class="star${faved ? ' on' : ''}">${faved ? '&#9733;' : '&#9734;'}</span>
    <div style="min-width:0"><div class="nm">${esc(s.name)} ${m.isNew ? '<span class="badge">NEW</span>' : ''}</div>
    <div class="meta">${esc(s.region)} &middot; ${m.players}/${s.maxPlayers} online &middot; ${pingText(ping)}</div></div>
    <div class="rt">${live ? '<span class="dot"></span> Live' : 'Offline'}<br>${m.daysLeft}d to wipe</div></div>`;
}

// ============================ synth + helpers =============================
// Population and ping are REAL: /api/servers reports each world's live connected-account count as `players`,
// and the menu measures a round-trip to each world's DO (see measurePings). Wipe/tags still derive from the id.
function pingText(ping: number | null | undefined): string {
  return ping === undefined ? '&hellip;' : ping === null ? '&mdash;' : `${ping} ms`;
}
function serverMeta(s: ServerInfo): { players: number; day: number; daysLeft: number; isNew: boolean; tags: string[] } {
  const h = hashStr(s.id);
  const players = typeof s.players === 'number' ? s.players : 0; // real live count from the server list
  const daysSince = Math.floor((Date.now() - WIPE_EPOCH) / 86400000);
  const cycleDay = ((daysSince % WIPE_DAYS) + WIPE_DAYS) % WIPE_DAYS;
  const tag2 = ['Vanilla', '2x', 'PvE', 'Modded', 'Hardcore'][h % 5];
  return { players, day: cycleDay + 1, daysLeft: WIPE_DAYS - cycleDay, isNew: cycleDay < 2, tags: ['PvP', tag2] };
}

// Free a Three.js subtree's geometry + materials (Object3D.remove and WebGLRenderer.dispose do NOT).
function disposeObject(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.geometry?.dispose?.();
      const mat = m.material;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat?.dispose?.();
    }
  });
}
function hex(n: number): string { return '#' + (n >>> 0).toString(16).padStart(6, '0'); }
function hashStr(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function esc(s: string): string { return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!)); }
function errMsg(e: unknown): string { return e instanceof Error ? e.message : 'Something went wrong.'; }
function randomSeed(): number { return Math.floor(Math.random() * 1_000_000_000); }

// 0..1 day-cycle fraction -> a "14:30"-style clock label (purely a display convenience; the fraction is
// what actually ships to the server, so this never has to match DAYNIGHT's real day/dusk/night/dawn split).
function timeLabel(frac: number): string {
  const totalMin = Math.round(frac * 24 * 60) % (24 * 60);
  const hh = Math.floor(totalMin / 60), mm = totalMin % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// --- per-world map cache: the small card preview ('oxide.map.') + the big viewer LOD ('oxide.maplod.'),
// keyed by world id AND seed so a wipe/reseed never shows a stale island. Deleting a world purges both.
function mapCacheKey(id: string, seed: number | undefined, lod: boolean): string { return `oxide.${lod ? 'maplod' : 'map'}.${id}:${seed ?? 'd'}`; }
function readMapCache(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function writeMapCache(key: string, canvas: HTMLCanvasElement): void { try { localStorage.setItem(key, canvas.toDataURL('image/webp', 0.85)); } catch { /* quota full: just re-bake next time */ } }
function purgeMapCache(id: string): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)!;
      if ((k.startsWith('oxide.map.') || k.startsWith('oxide.maplod.')) && k.includes(`.${id}:`)) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}

function loadFavServers(): string[] { try { const raw = localStorage.getItem(FAV_KEY); return raw ? (JSON.parse(raw) as string[]) : []; } catch { return []; } }
function saveFavServers(set: Set<string>): void { try { localStorage.setItem(FAV_KEY, JSON.stringify([...set])); } catch { /* ignore */ } }

// ============================ animated dusk-beach scene =============================
const SCENE = { skyTop: '#28324e', skyBot: '#e08a4e', sun: '#ffbf78', glow: 'rgba(255,150,80,.55)', mtn: '#33314e', mtn2: '#2c2b46', tree: '#26301f', sand: '#cbb488', water: '#4a5a72', palm: '#161a11' };
function shade(hexc: string, amt: number): string {
  const s = hexc.replace('#', ''); let r = parseInt(s.slice(0, 2), 16), g = parseInt(s.slice(2, 4), 16), b = parseInt(s.slice(4, 6), 16);
  const f = 1 + amt / 100; r = clamp255(r * f); g = clamp255(g * f); b = clamp255(b * f);
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}
function clamp255(v: number): number { return Math.max(0, Math.min(255, Math.round(v))); }
function palm(x: number, base: number, s: number, col: string): string {
  let fr = '';
  for (const a of [-155, -120, -90, -60, -25]) {
    const r = a * Math.PI / 180, len = 62; const ex = Math.cos(r) * len, ey = Math.sin(r) * len * 0.9;
    const cx = Math.cos(r) * len * 0.55 + (a < -90 ? -16 : 16), cy = Math.sin(r) * len * 0.55 - 14;
    fr += `<path d="M0 -128 Q ${cx} ${cy} ${ex} ${-128 + ey}" fill="none" stroke="${col}" stroke-width="7" stroke-linecap="round"/>`;
  }
  return `<g transform="translate(${x},${base}) scale(${s})"><path d="M0 0 Q -10 -64 6 -128" fill="none" stroke="${col}" stroke-width="9" stroke-linecap="round"/>${fr}</g>`;
}
function dust(n: number): string {
  let d = '';
  // Deterministic scatter (no Math.random needed): spread across the pane on staggered timings.
  for (let i = 0; i < n; i++) {
    const left = 8 + ((i * 37) % 84), bottom = 20 + ((i * 53) % 50), dur = 7 + ((i * 3) % 7), delay = (i * 0.7) % 8;
    d += `<span class="dust" style="left:${left}%;bottom:${bottom}%;animation:dustf ${dur}s linear ${delay}s infinite"></span>`;
  }
  return d;
}
function buildScene(host: HTMLDivElement): void {
  const g = SCENE;
  const mtn = `<div class="lay mtn" style="bottom:34%"><svg viewBox="0 0 1200 240" preserveAspectRatio="none"><polygon points="0,240 0,150 140,70 260,140 380,50 520,150 660,80 820,160 980,60 1120,150 1200,120 1200,240" fill="${g.mtn2}"/><polygon points="0,240 0,180 180,120 340,180 500,110 680,180 860,130 1040,180 1200,150 1200,240" fill="${g.mtn}"/></svg></div>`;
  const trees = `<div class="lay trees" style="bottom:30%"><svg viewBox="0 0 1200 120" preserveAspectRatio="none"><path d="M0 120 L0 70 Q 60 40 120 66 Q 180 30 250 60 Q 320 34 400 62 Q 480 32 560 60 Q 650 36 740 60 Q 820 34 900 62 Q 980 38 1070 60 Q 1140 42 1200 66 L1200 120 Z" fill="${g.tree}"/></svg></div>`;
  let sh = ''; for (let i = 0; i < 4; i++) sh += `<div class="shimmer" style="top:${20 + i * 16}%;animation:shim ${6 + i * 1.5}s ease-in-out ${i * 0.8}s infinite"></div>`;
  const water = `<div class="water" style="bottom:0;height:32%;background:linear-gradient(180deg,${g.water},${shade(g.water, -18)})">${sh}</div>`;
  const sand = `<div class="lay" style="bottom:0;height:26%"><svg viewBox="0 0 1200 200" preserveAspectRatio="none"><path d="M0 200 L0 90 Q 300 20 640 60 Q 940 96 1200 40 L1200 200 Z" fill="${g.sand}"/></svg></div>`;
  const palms = `<div class="lay palms" style="bottom:8%;height:60%"><svg viewBox="0 0 1200 420" preserveAspectRatio="xMidYMax slice">${palm(230, 300, 1.05, g.palm)}${palm(1000, 320, 0.9, g.palm)}${palm(1150, 300, 1.15, g.palm)}</svg></div>`;
  const sun = `<div class="sunglow" style="width:220px;height:220px;left:40%;top:20%;background:${g.glow}"></div><div class="sun" style="width:92px;height:92px;left:calc(40% + 30px);top:calc(20% + 30px);background:${g.sun}"></div>`;
  host.innerHTML = `<div class="lay" style="inset:0;left:0;right:0;width:100%;background:linear-gradient(180deg,${g.skyTop} 0%,${g.skyBot} 64%)"></div>${sun}${mtn}${trees}${water}${sand}${palms}<div class="lay" style="inset:0;left:0;right:0;width:100%">${dust(12)}</div>`;
}

// ============================ scoped stylesheet =============================
// The owner's design, scoped under `.oxmenu` so its generic selectors (.btn, .field, input[type=range], …)
// never leak onto the in-game UI.
function injectCss(): void {
  if (document.getElementById('oxmenu-style')) return;
  const s = document.createElement('style');
  s.id = 'oxmenu-style';
  s.textContent = OXMENU_CSS;
  document.head.appendChild(s);
}
const OXMENU_CSS = `
.oxmenu{--ink:${SK.textHi};--ink-dim:${SK.dim};--ink-faint:${SK.faint};--ox-red:${SK.accent};--ox-red-soft:${SK.accentHi};--green:${SK.good};--ox-bad:${SK.bad};
  --panel:${SK.bg1};--line:${SK.border};
  --mono:${SK.font};
  --sans:${SK.font};
  position:fixed;inset:0;z-index:30;display:flex;align-items:center;justify-content:center;padding:24px;
  background:${SK.bg0};background-image:radial-gradient(120% 80% at 50% -10%,#181310,${SK.bg0} 60%);
  color:${SK.text};font-family:var(--mono);-webkit-font-smoothing:antialiased;}
.oxmenu *{box-sizing:border-box;}
.oxmenu .window{position:relative;width:min(1300px,97vw);height:min(864px,90vh);border-radius:16px;overflow:hidden;
  display:grid;grid-template-columns:42% 58%;background:var(--panel);
  box-shadow:0 40px 120px rgba(0,0,0,.7),0 0 0 1px rgba(255,255,255,.05);}
.oxmenu[data-view="auth"] .app-only{display:none!important;}
.oxmenu[data-view="app"] .auth-only{display:none!important;}
.oxmenu .left{position:relative;overflow:hidden;}
.oxmenu .scene{position:absolute;inset:0;overflow:hidden;}
.oxmenu .scene .lay{position:absolute;left:-6%;right:-6%;width:112%;}
.oxmenu .scene svg{display:block;width:100%;height:auto;}
.oxmenu .sun{position:absolute;border-radius:50%;filter:blur(2px);}
.oxmenu .sunglow{position:absolute;border-radius:50%;filter:blur(48px);opacity:.85;}
.oxmenu .water{position:absolute;left:0;right:0;}
.oxmenu .shimmer{position:absolute;left:0;right:0;height:2px;background:rgba(255,255,255,.5);opacity:.24;}
.oxmenu .dust{position:absolute;width:3px;height:3px;border-radius:50%;background:rgba(255,240,210,.6);filter:blur(.4px);}
@keyframes driftA{0%{transform:translateX(0)}100%{transform:translateX(-24px)}}
@keyframes driftB{0%{transform:translateX(0)}100%{transform:translateX(-42px)}}
@keyframes driftC{0%{transform:translate(0,0)}50%{transform:translate(-12px,-3px)}100%{transform:translate(0,0)}}
@keyframes shim{0%{transform:translateX(-26px);opacity:.08}50%{opacity:.3}100%{transform:translateX(26px);opacity:.08}}
@keyframes dustf{0%{transform:translate(0,0);opacity:0}12%{opacity:.7}100%{transform:translate(36px,-130px);opacity:0}}
@keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}
@keyframes fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.oxmenu .mtn{animation:driftA 44s ease-in-out infinite alternate;}
.oxmenu .trees{animation:driftB 32s ease-in-out infinite alternate;}
.oxmenu .palms{animation:driftC 15s ease-in-out infinite;}
.oxmenu .left .scrim{position:absolute;inset:0;pointer-events:none;
  background:linear-gradient(100deg,rgba(6,7,6,.86) 0%,rgba(6,7,6,.5) 46%,rgba(6,7,6,.18) 100%),
             linear-gradient(180deg,rgba(6,7,6,.35),transparent 30%,rgba(6,7,6,.5));}
.oxmenu .left .vig{position:absolute;inset:0;pointer-events:none;box-shadow:inset 0 0 120px rgba(0,0,0,.5);}
.oxmenu .loverlay{position:absolute;inset:0;display:flex;flex-direction:column;padding:38px 34px;}
.oxmenu .wm{font-family:var(--mono);font-weight:700;letter-spacing:.26em;color:var(--ink);font-size:26px;}
.oxmenu .wm b{color:var(--ox-red);}
.oxmenu .tagline{font-family:var(--mono);font-size:13px;color:rgba(255,255,255,.72);max-width:270px;line-height:1.7;margin-top:14px;}
.oxmenu .devtag{font-family:var(--mono);font-size:10px;letter-spacing:2px;color:rgba(255,255,255,.4);margin-top:auto;}
.oxmenu .nav{display:flex;flex-direction:column;gap:5px;margin-top:30px;}
.oxmenu .nav a{display:block;font-family:var(--mono);font-size:14px;color:var(--ink-dim);padding:11px 14px;border-radius:9px;cursor:pointer;transition:.12s;letter-spacing:.02em;}
.oxmenu .nav a:hover{color:var(--ink);background:rgba(255,255,255,.06);}
.oxmenu .nav a.active{color:#fff;background:var(--ox-red);box-shadow:0 8px 22px rgba(192,71,42,.4);}
.oxmenu .foot{margin-top:auto;padding-top:16px;border-top:1px solid rgba(255,255,255,.1);font-family:var(--mono);}
.oxmenu .foot .lbl{font-size:9px;letter-spacing:1.6px;color:var(--ink-faint);text-transform:uppercase;}
.oxmenu .foot .usr{font-size:14px;color:var(--ink);margin:4px 0 12px;}
.oxmenu .foot .out{font-size:12.5px;color:var(--ink-dim);cursor:pointer;display:inline-block;}
.oxmenu .foot .out:hover{color:var(--ox-red);}
.oxmenu .right{position:relative;background:var(--panel);border-left:1px solid rgba(192,71,42,.32);overflow:hidden;}
.oxmenu .right::before{content:"";position:absolute;left:0;top:0;bottom:0;width:1px;background:linear-gradient(180deg,transparent,rgba(192,71,42,.5),transparent);}
.oxmenu .pad{position:absolute;inset:0;padding:40px 42px;overflow-y:auto;}
.oxmenu .ptitle{font-family:var(--mono);font-weight:700;font-size:20px;letter-spacing:.02em;}
.oxmenu .psub{color:var(--ink-dim);font-size:12.5px;margin-top:4px;margin-bottom:22px;}
.oxmenu .section-lbl{font-family:var(--mono);font-size:10px;letter-spacing:1.6px;color:var(--ink-faint);text-transform:uppercase;margin:22px 0 12px;}
.oxmenu .route{display:none;}
.oxmenu .route.on{display:block;animation:fade .22s ease-out;}
.oxmenu .auth-wrap{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:40px;}
.oxmenu .auth-card{width:min(400px,100%);}
.oxmenu .auth-card h2{font-family:var(--mono);font-weight:700;font-size:24px;letter-spacing:.06em;margin:0 0 6px;}
.oxmenu .auth-card p{color:var(--ink-dim);font-size:13px;font-family:var(--mono);margin:0 0 22px;}
.oxmenu .auth-err{font-family:var(--mono);font-size:12px;color:var(--ox-red-soft);min-height:16px;margin:0 0 8px;}
.oxmenu .field{width:100%;font-family:var(--mono);font-size:14px;color:var(--ink);background:${SK.well};border:1px solid var(--line);border-radius:9px;padding:13px 14px;outline:none;margin-bottom:12px;transition:border-color .12s,box-shadow .12s;}
.oxmenu .field:focus{border-color:var(--ox-red);box-shadow:0 0 0 3px ${SK.accentSoft};}
.oxmenu .field.code{text-align:center;letter-spacing:.6em;font-size:20px;padding:14px;}
.oxmenu .btn{width:100%;font-family:var(--mono);font-weight:700;font-size:13.5px;letter-spacing:.04em;border:1px solid var(--line);border-radius:9px;padding:13px;cursor:pointer;background:rgba(255,255,255,.04);color:var(--ink);transition:.12s;}
.oxmenu .btn:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.2);}
.oxmenu .btn:disabled{opacity:.6;cursor:default;}
.oxmenu .btn.primary{background:var(--ox-red);border-color:var(--ox-red);color:#fff6ec;}
.oxmenu .btn.primary:hover{background:var(--ox-red-soft);box-shadow:0 4px 18px ${SK.accentGlow};}
.oxmenu .btn.dev{background:${SK.goodSoft};border-color:var(--green);color:var(--green);margin-top:9px;}
.oxmenu .btn.dev:hover{background:rgba(111,179,95,.24);}
.oxmenu .btn.red{background:var(--ox-red);border-color:var(--ox-red);color:#fff6ec;}
.oxmenu .btn.red:hover{background:var(--ox-red-soft);box-shadow:0 4px 18px ${SK.accentGlow};}
.oxmenu .subtle{font-family:var(--mono);font-size:12px;color:var(--ink-faint);text-align:center;margin-top:14px;cursor:pointer;}
.oxmenu .subtle:hover{color:var(--ink-dim);}
.oxmenu .toolbar{display:flex;gap:10px;margin-bottom:16px;}
.oxmenu .search{flex:1;display:flex;align-items:center;gap:9px;border:1px solid var(--line);border-radius:9px;padding:0 13px;background:rgba(0,0,0,.28);}
.oxmenu .search input{flex:1;background:none;border:0;outline:none;color:var(--ink);font-family:var(--mono);font-size:13px;padding:12px 0;}
.oxmenu .search input::placeholder{color:var(--ink-faint);}
.oxmenu .chip{font-family:var(--mono);font-size:11px;color:var(--ink-dim);border:1px solid var(--line);border-radius:8px;padding:0 13px;line-height:34px;background:rgba(255,255,255,.02);cursor:pointer;transition:.12s;}
.oxmenu .chip.on{color:var(--ink);border-color:var(--ox-red);background:${SK.accentSoft};}
.oxmenu .playgrid{display:grid;grid-template-columns:1.3fr 1fr;gap:16px;}
.oxmenu .srlist{display:flex;flex-direction:column;gap:9px;max-height:540px;overflow-y:auto;}
.oxmenu .empty-note{font-family:var(--mono);font-size:12.5px;color:var(--ink-faint);padding:20px;}
.oxmenu .srow{display:flex;align-items:center;gap:12px;padding:13px 14px;border:1px solid var(--line);border-radius:11px;background:rgba(255,255,255,.02);cursor:pointer;transition:.12s;}
.oxmenu .srow:hover{background:rgba(255,255,255,.045);border-color:rgba(255,255,255,.16);}
.oxmenu .srow.sel{border-color:var(--ox-red);background:${SK.accentSoft};}
.oxmenu .srow .star,.oxmenu .star{color:var(--ink-faint);cursor:pointer;}
.oxmenu .star.on{color:var(--ox-red);}
.oxmenu .srow .nm{font-family:var(--mono);font-weight:600;font-size:13.5px;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.oxmenu .srow .meta{font-family:var(--mono);font-size:11px;color:var(--ink-dim);margin-top:2px;}
.oxmenu .srow .rt{margin-left:auto;text-align:right;font-family:var(--mono);font-size:11px;color:var(--ink-dim);flex:none;}
.oxmenu .badge{font-family:var(--mono);font-size:9px;letter-spacing:1px;color:#fff;background:var(--ox-red);border-radius:4px;padding:2px 5px;font-weight:700;}
.oxmenu .dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--green);box-shadow:0 0 8px var(--green);animation:pulse 1.8s ease-in-out infinite;}
.oxmenu .tag{font-family:var(--mono);font-size:10px;letter-spacing:.4px;color:var(--ink-dim);border:1px solid var(--line);border-radius:4px;padding:2px 7px;}
.oxmenu .pop{height:5px;border-radius:3px;background:rgba(255,255,255,.08);overflow:hidden;}
.oxmenu .pop>i{display:block;height:100%;border-radius:3px;background:linear-gradient(90deg,var(--ox-red-soft),var(--ox-red));}
.oxmenu .detail{border:1px solid var(--line);border-radius:12px;padding:16px;display:flex;flex-direction:column;gap:13px;min-height:480px;background:rgba(255,255,255,.015);}
.oxmenu .detail.empty{align-items:center;justify-content:center;color:var(--ink-faint);font-family:var(--mono);font-size:12.5px;text-align:center;}
.oxmenu .mapbox{position:relative;border-radius:10px;overflow:hidden;border:1px solid var(--line);background:#0c1418;flex:none;aspect-ratio:1/1;cursor:zoom-in;}
.oxmenu .mapN{position:absolute;top:8px;right:10px;font-family:var(--mono);font-size:10px;color:rgba(255,255,255,.5);letter-spacing:1px;z-index:1;}
.oxmenu .mapload{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:9px;z-index:2;font-family:var(--mono);font-size:11px;color:rgba(255,255,255,.72);}
.oxmenu .mbar{width:62%;height:4px;background:rgba(255,255,255,.14);border-radius:3px;overflow:hidden;}
.oxmenu .mbar i{display:block;height:100%;width:0;background:var(--ox-red);transition:width .12s linear;}
.oxmenu .kv{display:flex;justify-content:space-between;font-family:var(--mono);font-size:11.5px;color:var(--ink-dim);}
.oxmenu .kv span:last-child{color:var(--ink);}
.oxmenu .time-row{display:flex;align-items:center;gap:8px;padding:8px 0;flex-wrap:wrap;}
.oxmenu .time-lbl{font-family:var(--mono);font-size:11px;color:var(--ink-dim);flex:none;}
.oxmenu .time-row input[type=range]{flex:1;min-width:70px;}
.oxmenu .time-val{font-family:var(--mono);font-size:12px;font-weight:700;color:var(--ink);flex:none;min-width:38px;}
.oxmenu .time-lock{display:flex;align-items:center;gap:5px;font-family:var(--mono);font-size:11px;color:var(--ink-dim);flex:none;cursor:pointer;white-space:nowrap;}
.oxmenu .time-lock input{accent-color:var(--ox-red);cursor:pointer;}
.oxmenu .playerwrap{display:grid;grid-template-columns:300px 1fr;gap:26px;}
.oxmenu .preview{position:relative;border:1px solid var(--line);border-radius:12px;height:400px;overflow:hidden;background:radial-gradient(70% 60% at 50% 40%,#1c1a17,#0a0908 75%);}
.oxmenu .swrow{display:flex;align-items:center;gap:14px;margin-bottom:14px;}
.oxmenu .swrow .lbl{width:78px;font-family:var(--mono);font-size:13px;color:var(--ink);}
.oxmenu .swatches{display:flex;gap:8px;flex-wrap:wrap;}
.oxmenu .sw{width:30px;height:30px;border-radius:7px;cursor:pointer;border:2px solid transparent;transition:.12s;box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);}
.oxmenu .sw:hover{transform:translateY(-2px);}
.oxmenu .sw.on{border-color:var(--ink);}
.oxmenu .statgrid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:8px;}
.oxmenu .stat{border:1px solid var(--line);border-radius:10px;padding:16px;text-align:center;background:rgba(255,255,255,.015);}
.oxmenu .stat .v{font-family:var(--mono);font-weight:700;font-size:22px;color:var(--ink);}
.oxmenu .stat .k{font-family:var(--mono);font-size:10px;letter-spacing:1.2px;color:var(--ink-faint);text-transform:uppercase;margin-top:5px;}
.oxmenu .setrow{margin-bottom:6px;}
.oxmenu .setrow .top{display:flex;justify-content:space-between;align-items:baseline;font-family:var(--mono);font-size:13px;color:var(--ink);margin-bottom:8px;}
.oxmenu .setrow .top .val{color:var(--ink-dim);}
.oxmenu input[type=range]{-webkit-appearance:none;appearance:none;width:100%;height:6px;border-radius:3px;outline:none;cursor:pointer;
  background:linear-gradient(to right,var(--ox-red) 0%,var(--ox-red) var(--val,50%),rgba(255,255,255,.14) var(--val,50%),rgba(255,255,255,.14) 100%);}
.oxmenu input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:var(--ox-red);box-shadow:0 0 0 3px rgba(192,71,42,.25);}
.oxmenu input[type=range]::-moz-range-thumb{width:16px;height:16px;border:0;border-radius:50%;background:var(--ox-red);}
.oxmenu .checkrow{display:flex;align-items:center;justify-content:space-between;font-family:var(--mono);font-size:13px;color:var(--ink);padding:10px 0;border-bottom:1px solid rgba(255,255,255,.05);}
.oxmenu input[type=checkbox]{accent-color:var(--ox-red);width:18px;height:18px;cursor:pointer;}
.oxmenu .rangewrap{margin-bottom:18px;}
.oxmenu .socgrid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.oxmenu .soc{display:flex;gap:14px;align-items:flex-start;border:1px solid var(--line);border-left:3px solid var(--sc,var(--ox-red));border-radius:11px;padding:16px;background:rgba(255,255,255,.015);cursor:pointer;transition:.14s;text-decoration:none;}
.oxmenu .soc:hover{transform:translateY(-2px);background:rgba(255,255,255,.04);border-color:rgba(255,255,255,.18);}
.oxmenu .soc .ico{width:34px;height:34px;flex:none;display:flex;align-items:center;justify-content:center;border-radius:8px;background:rgba(255,255,255,.05);}
.oxmenu .soc .ico svg{width:20px;height:20px;fill:var(--sc);}
.oxmenu .soc .nm{font-family:var(--mono);font-weight:700;font-size:14px;color:var(--ink);}
.oxmenu .soc .ds{font-family:var(--mono);font-size:11.5px;color:var(--ink-dim);margin-top:4px;}
.oxmenu .soc .hd{font-family:var(--mono);font-size:10.5px;color:var(--sc);margin-top:7px;}
.oxmenu ::-webkit-scrollbar{width:9px;} .oxmenu ::-webkit-scrollbar-thumb{background:rgba(255,255,255,.1);border-radius:5px;} .oxmenu ::-webkit-scrollbar-track{background:transparent;}
/* RULES page: the loop as big numbered steps */
.oxmenu .rloop{display:flex;flex-direction:column;gap:10px;margin-top:6px;}
.oxmenu .rstep{display:flex;align-items:center;gap:14px;font-family:var(--mono);font-weight:700;font-size:19px;letter-spacing:.03em;color:var(--ink);border:1px solid var(--line);border-radius:11px;padding:14px 18px;background:rgba(255,255,255,.02);}
.oxmenu .rstep .n{width:30px;height:30px;flex:none;display:flex;align-items:center;justify-content:center;border-radius:8px;background:var(--ox-red);color:#fff;font-size:14px;}
/* Fullscreen map viewer + New World popup (shared overlay) */
.oxmenu-mapview{position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;background:rgba(4,4,5,.78);backdrop-filter:blur(3px);
  font-family:ui-monospace,"SF Mono","Cascadia Code",Menlo,Consolas,monospace;color:#e9e2d6;}
.oxmenu-mapview *{box-sizing:border-box;}
.oxmenu-mapview .mv-frame{display:flex;flex-direction:column;width:min(860px,94vw);max-height:94vh;background:#0a0b0c;border:1px solid rgba(255,255,255,.1);border-radius:14px;overflow:hidden;box-shadow:0 40px 120px rgba(0,0,0,.8);}
.oxmenu-mapview .mv-head{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid rgba(255,255,255,.08);font-size:13px;font-weight:700;letter-spacing:.03em;}
.oxmenu-mapview .mv-hint{font-size:10.5px;font-weight:400;color:#6b6154;}
.oxmenu-mapview .mv-btns{margin-left:auto;display:flex;gap:6px;}
.oxmenu-mapview .mv-btns button{width:30px;height:30px;border-radius:7px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.05);color:#e9e2d6;font-size:14px;cursor:pointer;}
.oxmenu-mapview .mv-btns button:hover{background:rgba(255,255,255,.12);}
.oxmenu-mapview .mv-body{position:relative;width:100%;aspect-ratio:1/1;max-height:calc(94vh - 56px);overflow:hidden;background:#0c1418;cursor:grab;touch-action:none;}
.oxmenu-mapview .mv-body:active{cursor:grabbing;}
.oxmenu-mapview .mv-img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;transform-origin:center;user-select:none;}
.oxmenu-mapview .mapload{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:9px;font-size:11px;color:rgba(255,255,255,.72);}
.oxmenu-mapview .mbar{width:42%;height:4px;background:rgba(255,255,255,.14);border-radius:3px;overflow:hidden;}
.oxmenu-mapview .mbar i{display:block;height:100%;width:0;background:#d0654a;transition:width .12s linear;}
.oxmenu-mapview .mapN{position:absolute;top:8px;right:10px;font-size:10px;color:rgba(255,255,255,.5);letter-spacing:1px;z-index:1;}
/* New World popup layout */
.oxmenu-mapview .mv-frame.nw{width:min(760px,94vw);}
.oxmenu-mapview .nw-body{display:grid;grid-template-columns:1.2fr 1fr;gap:16px;padding:16px;}
.oxmenu-mapview .nw-map{position:relative;aspect-ratio:1/1;border-radius:10px;overflow:hidden;border:1px solid rgba(255,255,255,.1);background:#0c1418;}
.oxmenu-mapview .nw-side{display:flex;flex-direction:column;gap:9px;}
.oxmenu-mapview .nw-side label{font-size:10px;letter-spacing:1.6px;color:#6b6154;text-transform:uppercase;margin-top:4px;}
.oxmenu-mapview .nw-side input{font-family:inherit;font-size:13px;color:#e9e2d6;background:rgba(0,0,0,.3);border:1px solid rgba(255,255,255,.12);border-radius:8px;padding:10px 12px;outline:none;}
.oxmenu-mapview .nw-side input:focus{border-color:#c0472a;}
.oxmenu-mapview .nw-seed{font-size:14px;font-weight:700;color:#e9e2d6;padding:4px 0;}
.oxmenu-mapview .nw-note{font-size:11px;line-height:1.6;color:#9a8f80;}
.oxmenu-mapview .time-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.oxmenu-mapview .time-row input[type=range]{flex:1;min-width:70px;}
.oxmenu-mapview .time-val{font-size:12px;font-weight:700;color:#e9e2d6;flex:none;min-width:38px;}
.oxmenu-mapview .time-lock{display:flex;align-items:center;gap:5px;font-size:10.5px;color:#9a8f80;flex:none;cursor:pointer;white-space:nowrap;text-transform:none;letter-spacing:normal;}
.oxmenu-mapview .time-lock input{accent-color:#c0472a;cursor:pointer;}
.oxmenu-mapview .nw-err{font-size:11.5px;color:#e06a6a;min-height:15px;}
.oxmenu-mapview .nw-side .btn{width:100%;font-family:inherit;font-weight:700;font-size:12.5px;letter-spacing:.04em;border:1px solid rgba(255,255,255,.12);border-radius:9px;padding:11px;cursor:pointer;background:rgba(255,255,255,.04);color:#e9e2d6;transition:.12s;}
.oxmenu-mapview .nw-side .btn:hover{background:rgba(255,255,255,.09);}
.oxmenu-mapview .nw-side .btn:disabled{opacity:.6;cursor:default;}
.oxmenu-mapview .nw-side .btn.red{background:#c0472a;border-color:#c0472a;color:#fff;margin-top:2px;}
.oxmenu-mapview .nw-side .btn.red:hover{background:#d0654a;}
`;
